import dynamic from "@redux-dynostore/react-redux";
import subspaced from "@redux-dynostore/react-redux-subspace";
import { attachReducer } from "@redux-dynostore/redux-subspace";
import React, { useCallback, useEffect, useState, useMemo } from "react";
import { connect } from "react-redux";

import styled, { withTheme } from "styled-components";

import moment from "moment";

import SmartFoodDrinkSelector from "../FoodDrinkSelector/FoodDrinkSelector";
import SmartDietDiaryCharts from "../Charts/DietDiaryCharts";
import DateAndTime from "../DateAndTime/DateAndTime/DateAndTime";
import CustomTabs, {
  CustomTabContent,
  TabButton,
} from "../../ui-library/CustomTabs/CustomTabs";

import NutritionTable from "../../library/NutritionTable/NutritionTable";
import { sendNotification } from "../../ToastManager/ToastManager";
import { ToastType } from "../../ToastManager/constants";

import AnimatedButton from "../../ui-library/Buttons/AnimatedButton/AnimatedButton";
import SecondaryButton from "../../ui-library/Buttons/SecondaryButton/SecondaryButton";
import PrimaryButton from "../../ui-library/Buttons/PrimaryButton/PrimaryButton";

import {
  changeRequestReducer,
  mapDispatchToProps,
  mapStateToProps,
} from "./ChangeRequest.store";
import DailyTargets from "../../library/Goals/DailyTargets";
import LabelledInput from "../../ui-library/LabelledInput/LabelledInput";
import Checkbox from "../../ui-library/Checkbox/Checkbox";
import { getInputTimeFormat, scrollModalToTop } from "../../../modules/utils";
import {
  ChangeRequestStatus,
  dailyTargetIds,
  DateFormat,
  getBaseDateFormat,
  getMacronutrientColor,
  noDateDefinedOrEmpty,
  PIE_CHART_SERIES_ATTRS,
  UNIT,
} from "../../../modules/constants";
import {
  addOtherSlice,
  calcKcalByAttrId,
  decimalPlaces,
  generateAboutMeal,
  roundMealPercentage,
  roundMealValue,
  UNKNOWN_NUTRIENT_VALUE,
} from "../../../helpers/mealsHelper";
import { useTranslation } from "react-i18next";
import { DEFAULT_MOMENT_LOCALE, isRtl } from "../../../i18n/helpers";
import {
  ADMINISTERED_BY_OPTIONS,
  FEEDING_TYPE_OPTIONS,
} from "../DietDiary/DietDiary.constants";
import CustomDropdown from "../../ui-library/CustomDropdown/CustomDropdown";
import DateStartEndTime from "../DateAndTime/DateStartEndTime/DateStartEndTime";

const ActionButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin-top: 38px;

  @media (max-width: 991px) {
    margin-top: 1rem;
    justify-content: space-between;
    & > button {
      width: 100%;
    }
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  flex-wrap: wrap;
`;

const LeftColWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 50%;

  @media (max-width: 991px) {
    width: 100%;
  }
`;

const RightColWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 50%;
  padding-left: 25px;
  margin-top: 0;

  @media (max-width: 991px) {
    width: 100%;
    padding-left: 0;
    margin-top: 30px;
  }
`;

const DateAndTimeRowWrapper = styled.div`
  display: flex;
  flex-direction: row;
`;

const DailyTargetsWrapper = styled.div`
  margin-top: 20px;
`;

const ReasonForChangeWrapper = styled.div`
  margin-top: 25px;
`;

const StyledCheckboxLabel = styled.div`
  font-weight: 600;
  margin-left: 15px;
  cursor: pointer;
`;

const StyledCheckboxWrapper = styled.div`
  display: flex;
  margin-top: 30px;
  margin-bottom: 15px;
`;

const ChangeToWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const ChangeToInnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px;
  background-color: ${(props) => props.theme?.whiteSmoke};
`;

const ChangeToDateAndTimeWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const ChangeToDateItem = styled.div`
  display: flex;
  flex-direction: column;

  h6 {
    margin-bottom: 5px;
    font-weight: 700;
  }

  span {
    padding: 2px 0px;
    font-size: 14px;
    font-weight: 600;
  }

  span.modified {
    color: ${(props) => props?.theme?.lightGreen};
    border: 2px solid ${(props) => props?.theme?.lightGreen};
    border-radius: 6px;
    text-align: center;
    padding: 2px 5px;
    width: min-content;
  }
`;

const ChangeToTimeItemsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: 20px;
`;

const ChangeToTimeItem = styled.div`
  display: flex;
  flex-direction: column;

  &:first-child {
    margin-right: 50px;
  }

  h6 {
    margin-bottom: 5px;
    font-weight: 700;
  }

  span {
    padding: 2px 0px;
    font-size: 14px;
    font-weight: 600;
  }

  span.modified {
    color: ${(props) => props?.theme?.lightGreen};
    border: 2px solid ${(props) => props?.theme?.lightGreen};
    border-radius: 6px;
    text-align: center;
    width: fit-content;
    padding: 2px 5px;
  }
`;

const ChangeToLegend = styled.div`
  margin-top: 30px;
  display: flex;
  flex-direction: column;
`;

const ChangeToLegendItem = styled.span`
  font-size: 16px;

  .modified {
    color: ${(props) => props?.theme?.lightGreen};
  }

  .deleted {
    color: ${(props) => props?.theme?.chiliRed};
  }
`;

const AdministrationMethodWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const AdministrationMethodRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 15px;
`;

const AdministrationMethodChangeToValue = styled.span`
  padding: 2px 5px;
  font-size: 14px;
  font-weight: 600;
  color: ${(props) =>
    props.modified ? props?.theme?.lightGreen : props?.theme?.gray};
  border: 2px solid
    ${(props) =>
      props.modified ? props?.theme?.lightGreen : props?.theme?.lightGray};
  border-radius: 6px;
`;

const FieldTitle = styled.span`
  color: ${(props) => props.theme?.nero};
  font-weight: 600;
  font-size: 16px;
`;

const DeletedMealInfoLabel = styled.span`
  width: 100%;
  padding: 2px 5px;
  font-size: 14px;
  font-weight: 600;
  background-color: ${(props) => props.theme?.lightRed};
  color: ${(props) => props.theme?.red};
  border: 2px solid ${(props) => props.theme?.red};
  border-radius: 6px;
  text-align: center;
  margin-top: -30px;
`;

export const SmartChangeRequest = (props) => {
  const {
    dailyTargets,
    nutritionalInfo,
    setNutritionalInfo,
    selectedItems,
    setSelectedItems,
    originalSelectedItems,
    setOriginalSelectedItems,
    calories,
    setCalories,
    chartsHelper,
    setChartsHelper,
    isSuccess,
    isError,
    setIsSuccess,
    setIsError,
    dateAndTimeValues,
    setDateAndTimeValues,
    originalDateAndTimeValues,
    setOriginalDateAndTimeValues,
    createChangeRequest,
    nutritionFields,
    fetchNutritionFields,
    setAboutMeal,
    aboutMeal,
    updateReasonFor,
    setUpdateReasonFor,
    resetStates,
    defaultItems,
    mealId,
    onSubmit,
    mealJsonData,
    minDate,
    maxDate,
    participantId,
    participantUsername,
    type,
    mealDate,
    setModalTitle,
    theme,
    entryMeal,
    changeRequest,
    readOnly,
    approveChangeRequest,
    declineChangeRequest,
    reasonForDecline,
    setReasonForDecline,
    participantLanguage,
    mealAdministeredBy,
    mealFeedingType,
    mealEndDate,
    refetchData,
    fetchParticipantTimeFormat,
    participantTimeFormat,
  } = props;

  const { t } = useTranslation();

  const [updateReasonForIsValid, setUpdateReasonForIsValid] = useState(false);

  const [confirm, setConfirm] = useState(false);
  const [startDate, setStartDate] = useState(new Date());
  const [confirmingItems, setConfirmingItems] = useState(false);
  const [dateTimeError, setDateTimeError] = useState(false);
  const [reasonForChangeError, setReasonForChangeError] = useState(null);
  const [reasonForDeclineError, setReasonForDeclineError] = useState(null);

  const [administeredByOption, setAdministeredByOption] = useState(null);
  const [feedingTypeOption, setFeedingTypeOption] = useState(null);

  const [originalAdministeredByOption, setOriginalAdministeredByOption] =
    useState(null);
  const [originalFeedingTypeOption, setOriginalFeedingTypeOption] =
    useState(null);

  const [tabActiveKey, setTabActiveKey] = useState("first");

  const [newItems, setNewItems] = useState([]);

  const btnSaveErrorMngmt = {
    isSuccess: isSuccess,
    setIsSuccess: setIsSuccess,
    isError: isError,
    setIsError: setIsError,
  };

  const administeredByOptions = useMemo(
    () =>
      Object.values(ADMINISTERED_BY_OPTIONS).map((options) => ({
        ...options,
        value: t(options.translationId),
      })),
    []
  );

  const feedingTypeOptions = useMemo(
    () =>
      Object.values(FEEDING_TYPE_OPTIONS).map((options) => ({
        ...options,
        value: t(options.translationId),
      })),
    []
  );

  useEffect(() => {
    if (participantId) {
      fetchParticipantTimeFormat(participantId);
    }
  }, [participantId, fetchParticipantTimeFormat]);

  const handleConfirm = () => {
    if (!selectedItems || selectedItems.length === 0) {
      setIsError(true);
      sendNotification(
        "You need to add at least one food or drink item.",
        ToastType.ERROR
      );
      return;
    }

    const anyItemWithoutQty = selectedItems.some(
      (item) => !item.qty || parseFloat(item.qty) === 0
    );
    if (anyItemWithoutQty) {
      setIsError(true);
      sendNotification(
        "You need to specify the quantity for all items",
        ToastType.ERROR
      );
      return;
    }

    if (confirmingItems) {
      submitHandler();
    } else {
      calculateItemsDiff();
      scrollModalToTop();
      setConfirmingItems(true);
    }
  };

  const handleApprove = () => {
    approveChangeRequest(changeRequest.key)
      .then((message) => {
        sendNotification(message, ToastType.SUCCESS);
        setIsSuccess(true);
        resetStates();
        onSubmit(true);
      })
      .catch((error) => {
        sendNotification(error, ToastType.ERROR);
        setIsError(true);
      });
  };

  const handleDecline = () => {
    if (!validateReasonForDecline(reasonForDecline)) {
      setIsError(true);
      return;
    }

    declineChangeRequest(changeRequest.key, reasonForDecline)
      .then((message) => {
        sendNotification(message, ToastType.SUCCESS);
        setIsSuccess(true);
        resetStates();
        onSubmit(true);
      })
      .catch((error) => {
        sendNotification(error, ToastType.ERROR);
        setIsError(true);
      });
  };

  const calculateItemsDiff = useCallback(() => {
    const items = [];
    const addedItems = [];

    selectedItems.forEach((item) => {
      let oldItem = originalSelectedItems.find(
        (x) => x.title.toLowerCase() === item.title.toLowerCase()
      );

      if (!oldItem) {
        const newItem = {
          ...item,
          addedOrModified: true,
        };

        addedItems.push(newItem);
      } else {
        oldItem = originalSelectedItems.find(
          (x) =>
            x.title.toLowerCase() === item.title.toLowerCase() &&
            x.qty === item.qty &&
            x.selectedMeasure.measure === item.selectedMeasure.measure
        );

        if (!oldItem) {
          oldItem = {
            ...item,
            addedOrModified: true,
          };
        }

        items.push(oldItem);
      }
    });

    originalSelectedItems.forEach((item, index) => {
      const oldItem = selectedItems.find(
        (x) => x.title.toLowerCase() === item.title.toLowerCase()
      );

      if (!oldItem) {
        items.splice(index, 0, { ...item, removed: true });
      }
    });

    items.push(...addedItems);
    setNewItems(items);
  }, [originalSelectedItems, selectedItems]);

  useEffect(() => {
    if (setModalTitle) {
      if (readOnly && changeRequest) {
        setModalTitle(`${participantUsername} entry`);
      } else if (!readOnly && changeRequest) {
        setModalTitle(
          `${
            changeRequest.requestor
          } requested a change for ${participantUsername} on ${moment(
            changeRequest.mealDate
          ).format(getBaseDateFormat())}`
        );
      } else {
        if (confirmingItems) {
          setModalTitle(
            `Please review ${participantUsername} entry before submitting`
          );
        } else {
          setModalTitle(`Editing ${participantUsername} entry`);
        }
      }
    }
  }, [
    confirmingItems,
    setModalTitle,
    participantUsername,
    changeRequest,
    readOnly,
  ]);

  const getNutrientById = useCallback(
    (searchAttrId) => {
      const info = nutritionalInfo.find(
        ({ attr_id }) => attr_id === searchAttrId
      );

      return info;
    },
    [nutritionalInfo]
  );

  const getNutrientUnitById = useCallback(
    (searchAttrId) => {
      const info = getNutrientById(searchAttrId);

      return info ? info.unit : "";
    },
    [getNutrientById]
  );

  const dailyTargetsArr = useMemo(() => {
    return {
      isRtl: isRtl(),
      goalTitle: t("appText.dietDiary.dailyTargets"),
      goalsList: [
        {
          goalLabel: t("appText.dietDiary.longChainFats"),
          goalColor: getMacronutrientColor(
            dailyTargetIds.LONG_CHAIN_FATS_ATTR_ID,
            theme
          ),
          goalValue: `${roundMealValue(
            dailyTargets?.longChainFatsPercentage ?? 0
          )}${UNIT.PERCENTAGE}`,
        },
        {
          goalLabel: t("appText.dietDiary.mediumChainFats"),
          goalColor: getMacronutrientColor(
            dailyTargetIds.MEDIUM_CHAIN_FATS_ATTR_ID,
            theme
          ),
          goalValue: `${roundMealValue(
            dailyTargets?.mediumChainFatsPercentage ?? 0
          )}${UNIT.PERCENTAGE}`,
        },
        {
          goalLabel: t("appText.dietDiary.protein"),
          goalColor: getMacronutrientColor(
            dailyTargetIds.PROTEIN_ATTR_ID,
            theme
          ),
          goalValue: `${roundMealValue(dailyTargets?.proteinPercentage ?? 0)}${
            UNIT.PERCENTAGE
          }`,
        },
        {
          goalLabel: t("appText.dietDiary.carbohydrates"),
          goalColor: getMacronutrientColor(dailyTargetIds.CARBS_ATTR_ID, theme),
          goalValue: `${roundMealValue(
            dailyTargets?.carbohydratesPercentage ?? 0
          )}${UNIT.PERCENTAGE}`,
        },
      ],
    };
  }, [dailyTargets, theme, t]);

  const setupChartsHelper = useCallback(() => {
    const chartInfoVals = PIE_CHART_SERIES_ATTRS.map((searchAttrId) => {
      const nutrient = nutritionalInfo.find(
        ({ attr_id }) => attr_id === searchAttrId
      );
      const numericValue = nutrient?.numericValue ?? 0.0;
      const title = nutrient
        ? isRtl()
          ? t(nutrient.translationId)
          : nutrient.chartTitle
        : "";
      const aboutMealLetter = nutrient ? nutrient.aboutMealLetter : "";
      const value = nutrient ? nutrient.value : UNKNOWN_NUTRIENT_VALUE;
      const unit = nutrient?.unit ?? "";

      return {
        attr_id: searchAttrId,
        letter: title,
        aboutMealLetter: aboutMealLetter,
        numericValue: numericValue,
        value: value === UNKNOWN_NUTRIENT_VALUE ? `0.0${unit}` : value,
      };
    });

    const total = calories.numericValue || 1;

    const tmpImpactVals = {};
    const chartVals = chartInfoVals.map((item) => {
      const frequency = roundMealPercentage(
        roundMealValue(
          (calcKcalByAttrId(item.numericValue, item.attr_id) / total) * 100.0
        )
      );

      tmpImpactVals[item.aboutMealLetter] = {
        value: item.numericValue,
        percentage: frequency,
        unit: getNutrientUnitById(item.attr_id),
      };

      const percentage = `${Number(frequency).toFixed(decimalPlaces)}${
        UNIT.PERCENTAGE
      }`;

      return {
        letter: item.letter,
        frequency: frequency,
        color: getMacronutrientColor(item.attr_id, theme),
        percentage: percentage,
        info: `${percentage} - ${item.value}`,
        isVisible: true,
      };
    });

    chartVals && chartVals.push(addOtherSlice(chartVals, theme, t));

    setChartsHelper({
      pieChartSeries: chartVals,
      hpbcImpact: tmpImpactVals,
    });

    setAboutMeal(generateAboutMeal(nutritionalInfo, total));
  }, [
    nutritionalInfo,
    setChartsHelper,
    setAboutMeal,
    getNutrientUnitById,
    theme,
    calories.numericValue,
    t,
  ]);

  useEffect(() => {
    const tmpCalories = nutritionalInfo.find(
      ({ attr_id }) => attr_id === dailyTargetIds.CALORIES_ATTR_ID
    );
    if (tmpCalories) {
      setCalories({
        percentage: 0,
        value: tmpCalories.value,
        numericValue: tmpCalories.numericValue,
        unit: getNutrientUnitById(dailyTargetIds.CALORIES_ATTR_ID),
      });
    }

    setupChartsHelper();
  }, [nutritionalInfo, setCalories, setupChartsHelper, getNutrientUnitById]);

  const validateReasonForChange = (reason) => {
    if (reason.replace(/\.|,/g, "").trim() === "") {
      setReasonForChangeError("Please provide a reason");
      setUpdateReasonForIsValid(false);
    } else if (reason.length > 450) {
      setReasonForChangeError(
        "Please enter a reason with less than 450 characters"
      );
      setUpdateReasonForIsValid(false);
    } else {
      setReasonForChangeError(null);
      setUpdateReasonForIsValid(true);
    }
  };

  useEffect(() => {
    if (updateReasonFor.replace(/\.|,/g, "").trim() !== "") {
      validateReasonForChange(updateReasonFor);
    }
  }, [updateReasonFor]);

  const backHandler = () => {
    if (readOnly) {
      onSubmit();
    } else {
      scrollModalToTop();
      setConfirm(false);
      setConfirmingItems(false);
      setUpdateReasonFor("");
      setUpdateReasonForIsValid(false);
      setReasonForChangeError(null);
    }
  };

  const validateReasonForDecline = (reason) => {
    if (reason.replace(/\.|,/g, "").trim() === "") {
      setReasonForDeclineError("Please provide a reason");
      return false;
    } else if (reason.length > 450) {
      setReasonForDeclineError(
        "Please enter a reason with less than 450 characters"
      );
      return false;
    } else {
      setReasonForDeclineError(null);
      return true;
    }
  };

  const submitHandler = useCallback(() => {
    const date = moment(dateAndTimeValues.dateAndTimeObj.date);
    const startTime = moment(dateAndTimeValues.dateAndTimeObj.startTime)
      .locale(DEFAULT_MOMENT_LOCALE)
      .format("HH:mm")
      .split(":");
    date.set({
      h: startTime[0],
      m: startTime[1],
    });

    const tmpAboutMeal = aboutMeal;

    tmpAboutMeal["Calories"] = {
      percentage: 0,
      value: calories.numericValue,
      unit: calories.unit,
    };

    const mealJson = {
      aboutMeal: tmpAboutMeal,
      items: selectedItems,
      type: type,
    };

    const formData = {
      mealDate: date
        .locale(DEFAULT_MOMENT_LOCALE)
        .format(DateFormat.EXTENDED_DATETIME),
      mealJson: JSON.stringify(mealJson),
      administeredBy: administeredByOption?.id,
      participantID: participantId,
    };

    if (administeredByOption?.feedingTypeRequired) {
      formData.feedingType = feedingTypeOption?.id;

      if (feedingTypeOption?.startEndTimeRequired) {
        const endDate = moment(dateAndTimeValues.dateAndTimeObj.date);
        const endTime = moment(dateAndTimeValues.dateAndTimeObj.endTime)
          .locale(DEFAULT_MOMENT_LOCALE)
          .format("HH:mm")
          .split(":");
        endDate.set({
          h: endTime[0],
          m: endTime[1],
        });

        formData.mealEndDate = endDate
          .locale(DEFAULT_MOMENT_LOCALE)
          .format(DateFormat.EXTENDED_DATETIME);
      }
    }

    if (updateReasonForIsValid) {
      formData.mealID = mealId;
      formData.reasonForChange = updateReasonFor;
    } else {
      setUpdateReasonForIsValid(false);
      validateReasonForChange(updateReasonFor);
      setIsError(true);
      return;
    }

    createChangeRequest(formData)
      .then((message) => {
        sendNotification(message, ToastType.SUCCESS);
        setIsSuccess(true);
        resetStates();
        onSubmit(true);
        refetchData();
      })
      .catch((error) => {
        sendNotification(error, ToastType.ERROR);
        setIsError(true);
      });
  }, [
    dateAndTimeValues,
    aboutMeal,
    calories,
    selectedItems,
    type,
    participantId,
    updateReasonForIsValid,
    createChangeRequest,
    mealId,
    updateReasonFor,
    setIsError,
    setIsSuccess,
    resetStates,
    onSubmit,
    administeredByOption,
    feedingTypeOption,
  ]);

  useEffect(() => {
    return () => {
      resetStates();
    };
  }, [resetStates]);

  useEffect(() => {
    setStartDate(dateAndTimeValues?.dateAndTimeObj?.date ?? new Date());
  }, [dateAndTimeValues]);

  useEffect(() => {
    if (mealJsonData) {
      const mealData = JSON.parse(mealJsonData);
      setAboutMeal(mealData?.aboutMeal);
      setSelectedItems(mealData?.items);
      setOriginalSelectedItems(mealData?.items);
    }
  }, [mealJsonData, setAboutMeal, setOriginalSelectedItems, setSelectedItems]);

  const onChangeDate = useCallback(
    (isDateAndTimeValid, dateAndTimeObj) => {
      setDateAndTimeValues({
        isValid: isDateAndTimeValid,
        dateAndTimeObj,
      });
    },
    [setDateAndTimeValues]
  );

  useEffect(() => {
    if (changeRequest) {
      setUpdateReasonFor(changeRequest.reasonForChange);
      const originalMealJson = JSON.parse(changeRequest.originalMealJson);
      setOriginalSelectedItems(originalMealJson?.items);

      const mealJson = JSON.parse(changeRequest.mealJson);
      setSelectedItems(mealJson?.items);
      setAboutMeal(mealJson?.aboutMeal);

      onChangeDate(true, {
        date: moment(changeRequest.mealDate).toDate(),
        startTime: moment(changeRequest.mealDate).toDate(),
        endTime: moment(
          changeRequest.endDate === noDateDefinedOrEmpty
            ? new Date()
            : changeRequest.endDate
        ).toDate(),
      });

      setAdministeredByOption(
        administeredByOptions.find(
          (option) => option.id === changeRequest.administration
        )
      );

      setFeedingTypeOption(
        feedingTypeOptions.find(
          (option) => option.id === changeRequest.feedingType
        )
      );

      setOriginalAdministeredByOption(
        administeredByOptions.find(
          (option) => option.id === changeRequest.originalAdministration
        )
      );

      setOriginalFeedingTypeOption(
        feedingTypeOptions.find(
          (option) => option.id === changeRequest.originalFeedingType
        )
      );

      setOriginalDateAndTimeValues({
        isValid: true,
        dateAndTimeObj: {
          date: moment(changeRequest.originalMealDate).toDate(),
          startTime: moment(changeRequest.originalMealDate).toDate(),
          endTime: moment(
            changeRequest.originalEndDate === noDateDefinedOrEmpty
              ? new Date()
              : changeRequest.originalEndDate
          ).toDate(),
        },
      });

      setConfirmingItems(true);
    }
  }, [
    changeRequest,
    onChangeDate,
    setAboutMeal,
    setOriginalDateAndTimeValues,
    setOriginalSelectedItems,
    setSelectedItems,
    setUpdateReasonFor,
    administeredByOptions,
    feedingTypeOptions,
  ]);

  useEffect(() => {
    if (selectedItems && originalSelectedItems && changeRequest) {
      calculateItemsDiff();
    }
  }, [selectedItems, originalSelectedItems, calculateItemsDiff, changeRequest]);

  useEffect(() => {
    if (mealDate) {
      onChangeDate(true, {
        date: moment(mealDate).toDate(),
        startTime: moment(mealDate).toDate(),
        endTime: moment(
          mealEndDate === noDateDefinedOrEmpty ? new Date() : mealEndDate
        ).toDate(),
      });

      setOriginalDateAndTimeValues({
        isValid: true,
        dateAndTimeObj: {
          date: moment(mealDate).toDate(),
          startTime: moment(mealDate).toDate(),
          endTime: moment(
            mealEndDate === noDateDefinedOrEmpty ? new Date() : mealEndDate
          ).toDate(),
        },
      });
    }
  }, [mealDate, onChangeDate, setOriginalDateAndTimeValues, mealEndDate]);

  const handleKeyPressCheckbox = (e) => {
    if (e.key === "Enter") {
      setConfirm((prevState) => !prevState);
    }
  };

  const tableHeaders = [{ label: "Description" }, { label: "Quantity" }];

  const isModified = (oldDateTime, newDateTime) => {
    return oldDateTime !== newDateTime ? "modified" : null;
  };

  useEffect(() => {
    if (mealAdministeredBy) {
      setAdministeredByOption(
        administeredByOptions.find((option) => option.id === mealAdministeredBy)
      );

      setOriginalAdministeredByOption(
        administeredByOptions.find((option) => option.id === mealAdministeredBy)
      );
    } else if (!changeRequest) {
      setAdministeredByOption(
        administeredByOptions.find(
          (option) => option.id === ADMINISTERED_BY_OPTIONS.MOUTH.id
        )
      );
    }
  }, [mealAdministeredBy, administeredByOptions, changeRequest]);

  useEffect(() => {
    if (mealFeedingType) {
      setFeedingTypeOption(
        feedingTypeOptions.find((option) => option.id === mealFeedingType)
      );

      setOriginalFeedingTypeOption(
        feedingTypeOptions.find((option) => option.id === mealFeedingType)
      );
    } else if (!changeRequest) {
      setFeedingTypeOption(
        feedingTypeOptions.find(
          (option) => option.id === FEEDING_TYPE_OPTIONS.CONTINUOUS.id
        )
      );
    }
  }, [mealFeedingType, feedingTypeOptions, changeRequest]);

  const administeredByOptionDefaultValue = useMemo(() => {
    if (confirmingItems) {
      return administeredByOptions?.find(
        (option) => option.id === originalAdministeredByOption?.id
      );
    }

    return administeredByOptions?.find(
      (option) => option.id === administeredByOption?.id
    );
  }, [
    confirmingItems,
    administeredByOptions,
    originalAdministeredByOption,
    administeredByOption,
  ]);

  const feedingTypeOptionDefaultValue = useMemo(() => {
    if (confirmingItems) {
      return feedingTypeOptions?.find(
        (option) => option.id === originalFeedingTypeOption?.id
      );
    }

    return feedingTypeOptions?.find(
      (option) => option.id === feedingTypeOption?.id
    );
  }, [
    confirmingItems,
    feedingTypeOptions,
    originalFeedingTypeOption,
    feedingTypeOption,
  ]);

  return (
    <Container data-testid="change-request">
      <Row>
        <LeftColWrapper>
          {confirmingItems ? (
            <SmartFoodDrinkSelector
              selectedItems={originalSelectedItems}
              readOnly={true}
              participantLanguage={participantLanguage}
              title={
                changeRequest
                  ? `Original - ${moment(changeRequest.originalMealDate).format(
                      getBaseDateFormat()
                    )}`
                  : "Original"
              }
            />
          ) : (
            <SmartFoodDrinkSelector
              defaultItems={defaultItems}
              selectedItems={selectedItems}
              setSelectedItems={setSelectedItems}
              readOnly={confirmingItems}
              inputTitle="Add new item"
              participantLanguage={participantLanguage}
            />
          )}

          <AdministrationMethodWrapper>
            <AdministrationMethodRow>
              <FieldTitle className="me-2">
                {t("appText.dietDiary.administeredBy.label")}
              </FieldTitle>
              {confirmingItems ? (
                <CustomDropdown
                  data-testid="administered-by-dropdown"
                  value={originalAdministeredByOption?.value}
                  disabled={true}
                  width={145}
                  align={isRtl() ? "end" : "start"}
                />
              ) : (
                <CustomDropdown
                  data-testid="administered-by-dropdown"
                  defaultValue={administeredByOptionDefaultValue?.value}
                  value={administeredByOption?.value}
                  options={administeredByOptions}
                  disabled={readOnly}
                  width={145}
                  onChange={(value) => setAdministeredByOption(value)}
                  limitheight={true}
                  align={isRtl() ? "end" : "start"}
                />
              )}
            </AdministrationMethodRow>

            {confirmingItems &&
            originalAdministeredByOption?.feedingTypeRequired ? (
              <AdministrationMethodRow>
                <FieldTitle className="me-2">
                  {t("appText.dietDiary.feedingType.label")}
                </FieldTitle>
                <CustomDropdown
                  data-testid="feeding-type-dropdown"
                  defaultValue={feedingTypeOptionDefaultValue?.value}
                  disabled={true}
                  width={145}
                  align={isRtl() ? "end" : "start"}
                />
              </AdministrationMethodRow>
            ) : (
              administeredByOptionDefaultValue?.feedingTypeRequired && (
                <AdministrationMethodRow>
                  <FieldTitle className="me-2">
                    {t("appText.dietDiary.feedingType.label")}
                  </FieldTitle>
                  <CustomDropdown
                    data-testid="feeding-type-dropdown"
                    defaultValue={feedingTypeOptionDefaultValue?.value}
                    value={feedingTypeOption?.value}
                    options={feedingTypeOptions}
                    disabled={readOnly}
                    width={145}
                    onChange={(value) => setFeedingTypeOption(value)}
                    limitheight={true}
                    align={isRtl() ? "end" : "start"}
                  />
                </AdministrationMethodRow>
              )
            )}
          </AdministrationMethodWrapper>

          <DateAndTimeRowWrapper>
            {administeredByOptionDefaultValue?.feedingTypeRequired &&
            feedingTypeOptionDefaultValue?.startEndTimeRequired ? (
              <DateStartEndTime
                maxDate={maxDate}
                minDate={minDate}
                onChange={onChangeDate}
                date={
                  confirmingItems
                    ? originalDateAndTimeValues?.dateAndTimeObj?.date
                    : dateAndTimeValues?.dateAndTimeObj?.date
                }
                readOnly={confirmingItems || readOnly}
                defaultDate={
                  confirmingItems
                    ? originalDateAndTimeValues?.dateAndTimeObj?.date
                    : dateAndTimeValues?.dateAndTimeObj?.date
                }
                defaultStartTime={
                  confirmingItems
                    ? originalDateAndTimeValues?.dateAndTimeObj?.startTime
                    : dateAndTimeValues?.dateAndTimeObj?.startTime
                }
                defaultEndTime={
                  confirmingItems
                    ? originalDateAndTimeValues?.dateAndTimeObj?.endTime
                    : dateAndTimeValues?.dateAndTimeObj?.endTime
                }
                onError={setDateTimeError}
                timeIntervals={5}
                timeFormat={participantTimeFormat}
              />
            ) : (
              <DateAndTime
                onChange={onChangeDate}
                maxDate={maxDate}
                minDate={minDate}
                readOnly={confirmingItems || readOnly}
                defaultDate={
                  confirmingItems
                    ? originalDateAndTimeValues?.dateAndTimeObj?.date
                    : dateAndTimeValues?.dateAndTimeObj?.date
                }
                defaultTime={
                  confirmingItems
                    ? originalDateAndTimeValues?.dateAndTimeObj?.startTime
                    : dateAndTimeValues?.dateAndTimeObj?.startTime
                }
                onError={setDateTimeError}
                timeIntervals={5}
                dateClassName="date-select-wrapper me-4"
                timeClassName="time-wrapper"
                timeFormat={participantTimeFormat}
              />
            )}
          </DateAndTimeRowWrapper>

          {dailyTargetsArr && !changeRequest && (
            <DailyTargetsWrapper>
              <DailyTargets {...dailyTargetsArr} />
            </DailyTargetsWrapper>
          )}
        </LeftColWrapper>
        <RightColWrapper>
          {confirmingItems ? (
            <ChangeToWrapper>
              <SmartFoodDrinkSelector
                hasBackground={true}
                selectedItems={newItems}
                readOnly={true}
                title="Change to"
                participantLanguage={participantLanguage}
                showData={!changeRequest?.toDelete}
              />

              <ChangeToInnerContainer>
                {changeRequest?.toDelete ? (
                  <DeletedMealInfoLabel>
                    {t("appText.dietDiary.changeRequest.deletedMeal")}
                  </DeletedMealInfoLabel>
                ) : (
                  <>
                    <AdministrationMethodWrapper>
                      <AdministrationMethodRow>
                        <FieldTitle className="me-2">
                          {t("appText.dietDiary.administeredBy.label")}
                        </FieldTitle>
                        <AdministrationMethodChangeToValue
                          modified={
                            originalAdministeredByOption?.id !==
                            administeredByOption.id
                          }
                        >
                          {administeredByOption.value}
                        </AdministrationMethodChangeToValue>
                      </AdministrationMethodRow>

                      {administeredByOption?.feedingTypeRequired && (
                        <AdministrationMethodRow>
                          <FieldTitle className="me-2">
                            {t("appText.dietDiary.feedingType.label")}
                          </FieldTitle>
                          <AdministrationMethodChangeToValue
                            modified={
                              originalFeedingTypeOption?.id !==
                              feedingTypeOption.id
                            }
                          >
                            {feedingTypeOption.value}
                          </AdministrationMethodChangeToValue>
                        </AdministrationMethodRow>
                      )}
                    </AdministrationMethodWrapper>

                    <ChangeToDateAndTimeWrapper>
                      <ChangeToDateItem>
                        <h6>Date</h6>

                        <span
                          className={isModified(
                            moment(
                              originalDateAndTimeValues?.dateAndTimeObj?.date
                            ).format(getBaseDateFormat()),
                            moment(
                              dateAndTimeValues?.dateAndTimeObj?.date
                            )?.format(getBaseDateFormat())
                          )}
                        >
                          {moment(
                            dateAndTimeValues?.dateAndTimeObj?.date
                          ).format(getBaseDateFormat())}
                        </span>
                      </ChangeToDateItem>

                      <ChangeToTimeItemsWrapper>
                        {administeredByOption?.feedingTypeRequired &&
                        feedingTypeOption?.startEndTimeRequired ? (
                          <>
                            <ChangeToTimeItem>
                              <h6>Start Time</h6>
                              <span
                                className={isModified(
                                  moment(
                                    originalDateAndTimeValues?.dateAndTimeObj
                                      ?.startTime
                                  ).format(
                                    getInputTimeFormat(participantTimeFormat)
                                  ),
                                  moment(
                                    dateAndTimeValues?.dateAndTimeObj?.startTime
                                  )?.format(
                                    getInputTimeFormat(participantTimeFormat)
                                  )
                                )}
                              >
                                {moment(
                                  dateAndTimeValues?.dateAndTimeObj?.startTime
                                ).format(
                                  getInputTimeFormat(participantTimeFormat)
                                )}
                              </span>
                            </ChangeToTimeItem>
                            <ChangeToTimeItem>
                              <h6>End Time</h6>
                              <span
                                className={isModified(
                                  originalFeedingTypeOption?.startEndTimeRequired
                                    ? moment(
                                        originalDateAndTimeValues
                                          ?.dateAndTimeObj?.endTime
                                      ).format(
                                        getInputTimeFormat(
                                          participantTimeFormat
                                        )
                                      )
                                    : null,
                                  moment(
                                    dateAndTimeValues?.dateAndTimeObj?.endTime
                                  )?.format(
                                    getInputTimeFormat(participantTimeFormat)
                                  )
                                )}
                              >
                                {moment(
                                  dateAndTimeValues?.dateAndTimeObj?.endTime
                                ).format(
                                  getInputTimeFormat(participantTimeFormat)
                                )}
                              </span>
                            </ChangeToTimeItem>
                          </>
                        ) : (
                          <ChangeToTimeItem>
                            <h6>Time</h6>
                            <span
                              className={isModified(
                                moment(
                                  originalDateAndTimeValues?.dateAndTimeObj
                                    ?.startTime
                                ).format(
                                  getInputTimeFormat(participantTimeFormat)
                                ),
                                moment(
                                  dateAndTimeValues?.dateAndTimeObj?.startTime
                                )?.format(
                                  getInputTimeFormat(participantTimeFormat)
                                )
                              )}
                            >
                              {moment(
                                dateAndTimeValues?.dateAndTimeObj?.startTime
                              ).format(
                                getInputTimeFormat(participantTimeFormat)
                              )}
                            </span>
                          </ChangeToTimeItem>
                        )}
                      </ChangeToTimeItemsWrapper>
                    </ChangeToDateAndTimeWrapper>
                  </>
                )}

                <ChangeToLegend>
                  <ChangeToLegendItem>
                    <span className="modified">&#x25A2;modified/added</span>{" "}
                    items.
                  </ChangeToLegendItem>
                  <ChangeToLegendItem>
                    <span className="deleted">&#x25A2;deleted</span> items.
                  </ChangeToLegendItem>
                </ChangeToLegend>
              </ChangeToInnerContainer>
            </ChangeToWrapper>
          ) : (
            <CustomTabs
              defaultActiveKey="first"
              onSelect={(eventKey) => {
                setTabActiveKey(eventKey);
              }}
            >
              <TabButton eventKey="first">About this Meal</TabButton>
              <TabButton eventKey="second">Nutrition Details</TabButton>
              <CustomTabContent eventKey="first">
                {selectedItems.length > 0 && (
                  <SmartDietDiaryCharts
                    participantId={participantId}
                    constants={dailyTargetIds}
                    getNutrientById={getNutrientById}
                    calories={calories}
                    pieChartSeries={chartsHelper.pieChartSeries}
                    hpbcImpact={chartsHelper.hpbcImpact}
                    startDate={startDate}
                    getMacronutrientColor={getMacronutrientColor}
                    mealId={mealId}
                    rerenderPieChart={tabActiveKey === "first"}
                  />
                )}
              </CustomTabContent>
              <CustomTabContent eventKey="second">
                <NutritionTable
                  nutritionalInfo={nutritionalInfo}
                  setNutritionalInfo={setNutritionalInfo}
                  nutritionFields={nutritionFields}
                  fetchNutritionFields={fetchNutritionFields}
                  selectedItems={selectedItems}
                  tableHeaders={tableHeaders}
                />
              </CustomTabContent>
            </CustomTabs>
          )}

          {changeRequest && updateReasonFor && (
            <ReasonForChangeWrapper>
              <LabelledInput
                title="Reason for change"
                type="text"
                data-testid="update-reason-input"
                value={updateReasonFor}
                disabled={true}
              />
            </ReasonForChangeWrapper>
          )}

          {mealId && confirmingItems && !changeRequest && (
            <ReasonForChangeWrapper>
              <LabelledInput
                title="Reason for change"
                type="text"
                data-testid="update-reason-input"
                onBlur={(e) => validateReasonForChange(e.target.value)}
                hasError={!!reasonForChangeError}
                errorMessage={reasonForChangeError}
                value={updateReasonFor}
                onChange={(e) => setUpdateReasonFor(e.target.value)}
              />
            </ReasonForChangeWrapper>
          )}

          {changeRequest &&
            readOnly &&
            !!changeRequest.reasonForDecline &&
            changeRequest.status === ChangeRequestStatus.DECLINED.id && (
              <ReasonForChangeWrapper>
                <LabelledInput
                  title="Reason for decline"
                  data-testid="reason-decline-input"
                  type="text"
                  value={changeRequest.reasonForDecline}
                  disabled={true}
                />
              </ReasonForChangeWrapper>
            )}

          {changeRequest &&
            changeRequest.status === ChangeRequestStatus.OPEN.id &&
            !readOnly && (
              <ReasonForChangeWrapper>
                <LabelledInput
                  title="Reason for decline"
                  type="text"
                  data-testid="reason-decline-input"
                  hasError={!!reasonForDeclineError}
                  errorMessage={reasonForDeclineError}
                  value={reasonForDecline}
                  onChange={(e) => setReasonForDecline(e.target.value)}
                />
              </ReasonForChangeWrapper>
            )}

          {confirmingItems && !changeRequest && (
            <StyledCheckboxWrapper
              tabIndex="0"
              onKeyPress={handleKeyPressCheckbox}
            >
              <Checkbox
                data-testid="confirm-checkbox"
                checked={confirm}
                onClick={() => setConfirm((prevState) => !prevState)}
                icon={true}
                square={true}
              />
              <StyledCheckboxLabel
                data-testid="confirm-checkbox-label"
                onClick={() => setConfirm((prevState) => !prevState)}
              >
                I confirm that the data change conversation has been logged in
                the subject's study chart.
              </StyledCheckboxLabel>
            </StyledCheckboxWrapper>
          )}
        </RightColWrapper>
      </Row>

      {(entryMeal || changeRequest) && (
        <ActionButtonsContainer>
          {confirmingItems && !changeRequest && (
            <SecondaryButton
              data-testid="back-button"
              className="me-3"
              onClick={() => backHandler()}
            >
              Back
            </SecondaryButton>
          )}

          {readOnly && changeRequest && (
            <PrimaryButton
              data-testid="close-button"
              onClick={() => backHandler()}
            >
              Close
            </PrimaryButton>
          )}

          {!readOnly && changeRequest && (
            <>
              <AnimatedButton
                variant="secondary"
                className="me-3"
                icon={null}
                data-testid="decline-button"
                onClick={() => handleDecline()}
                {...btnSaveErrorMngmt}
              >
                Decline
              </AnimatedButton>

              <AnimatedButton
                icon={null}
                data-testid="approve-button"
                onClick={() => handleApprove()}
                {...btnSaveErrorMngmt}
              >
                Approve
              </AnimatedButton>
            </>
          )}

          {readOnly || changeRequest ? (
            <div></div>
          ) : confirmingItems ? (
            <AnimatedButton
              data-testid="submit-button"
              icon={null}
              onClick={() => handleConfirm()}
              disabled={
                !confirm || reasonForChangeError || !updateReasonForIsValid
              }
              keepBorder={true}
              {...btnSaveErrorMngmt}
            >
              Request Change
            </AnimatedButton>
          ) : (
            <PrimaryButton
              data-testid="next-button"
              disabled={dateTimeError}
              onClick={() => handleConfirm()}
            >
              Next
            </PrimaryButton>
          )}
        </ActionButtonsContainer>
      )}
    </Container>
  );
};

export default dynamic(
  "changeRequestReducer",
  subspaced({
    // We map extra state because we might need to acess other stores for data, subspace is self contained.
    mapExtraState: (state) => {
      return {
        loginReducer: state.loginReducer,
        dietDiaryChartsReducer:
          state.changeRequestReducer.dietDiaryChartsReducer,
      };
    },
  }),
  attachReducer(changeRequestReducer)
)(connect(mapStateToProps, mapDispatchToProps)(withTheme(SmartChangeRequest)));
